<template>
    <div
        :id="editorId"
        :style="sizeStyle"
        v-if="todisplay"
        v-show="isvisible"
        :class="{ 'c-FileDiff': true, 'c-FileDiffv2': true, [`c-name-${name || 'unnamed'}`]: true }"
    >
        <div ref="layoutRoot" class="c-FileDiff__inner c-FileDiffv2__inner">
            <QBtn
                v-show="!isFetchingDiffContent"
                class="c-FileDiffv2__debug-btn"
                flat
                dense
                round
                icon="info"
                aria-label="File diff performance metrics"
                tabindex="0"
            >
                <QTooltip
                    anchor="bottom left"
                    self="top right"
                    :delay="200"
                    :offset="[0, 6]"
                    class="c-FileDiffv2__debug-tooltip bg-grey-9 text-white"
                >
                    <div class="c-FileDiffv2__debug-tooltip-inner">
                        <div class="c-FileDiffv2__debug-tooltip-title">File diff metrics</div>
                        <table class="c-FileDiffv2__debug-table">
                            <tbody>
                                <tr v-for="row in debugMetricRows" :key="row.key">
                                    <th scope="row">{{ row.label }}</th>
                                    <td>{{ row.value }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </QTooltip>
            </QBtn>
            <div v-show="!isFetchingDiffContent" class="c-FileDiffv2__toolbar">
                <QBtn
                    dense
                    flat
                    icon="chevron_left"
                    label="Previous"
                    :disable="!diffEditorReady"
                    @click="PrevDiff"
                />
                <QBtn
                    dense
                    flat
                    icon-right="chevron_right"
                    label="Next"
                    :disable="!diffEditorReady"
                    @click="NextDiff"
                />
            </div>
            <div
                v-show="!isFetchingDiffContent"
                ref="editorWrap"
                class="c-FileDiffv2__editor-wrap"
            >
                <div v-if="showPaneTitles" class="c-FileDiffv2__pane-titles">
                    <div class="c-FileDiffv2__pane-title c-FileDiffv2__pane-title--left">{{ displayTitleLeft }}</div>
                    <div class="c-FileDiffv2__pane-title c-FileDiffv2__pane-title--right">{{ displayTitleRight }}</div>
                </div>
                <MonacoDiffEditor
                    ref="diffEditor"
                    class="c-FileDiffv2__editor"
                    :editor-id="editorId"
                    :original-content="displayOriginalContent"
                    :modified-content="displayModifiedContent"
                    :lang="controlData.TextType || 'text/plain'"
                    :read-only="true"
                    @init="onDiffEditorInit"
                    @metrics-update="onMonacoMetricsUpdate"
                />
            </div>
            <div v-if="isFetchingDiffContent" class="c-FileDiff__loading" aria-busy="true">
                <QSpinner color="primary" size="48px" :thickness="5" />
            </div>
        </div>
    </div>
</template>

<script>
    import utils from '@/Shared/utils.jsx';
    import BaseComponent from './BaseComponentMixin.jsx';
    import api from '@/Services/api';
    import { QSpinner, QBtn, QTooltip } from 'quasar';
    import MonacoDiffEditor from './3rdParty/MonacoDiffEditor.jsx';

    function countLines(text) {
        const s = text == null ? '' : String(text);
        if (!s.length)
            return 0;
        let lines = 1;
        for (let i = 0; i < s.length; i++) {
            if (s.charCodeAt(i) === 10)
                lines++;
        }
        return lines;
    }

    function formatMs(ms) {
        if (ms == null || Number.isNaN(ms))
            return '—';
        if (ms < 1000)
            return `${Math.round(ms)} ms`;
        return `${(ms / 1000).toFixed(2)} s`;
    }

    function formatBytes(bytes) {
        if (bytes == null || Number.isNaN(bytes))
            return '—';
        if (bytes < 1024)
            return `${bytes} B`;
        if (bytes < 1024 * 1024)
            return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }

    function createFileDiffDebugMetrics() {
        return {
            leftLoadMs: null,
            rightLoadMs: null,
            leftFormatMs: null,
            rightFormatMs: null,
            fetchTotalMs: null,
            revealFirstDiffMs: null,
            totalToFirstDiffMs: null,
            leftSource: null,
            rightSource: null,
            leftLines: null,
            rightLines: null,
            leftBytes: null,
            rightBytes: null,
        };
    }

    export default {
        name: 'FileDiffv2',
        mixins: [BaseComponent],
        components: { QSpinner, QBtn, QTooltip, MonacoDiffEditor },
        data() {
            return {
                editorId: null,
                urlLeftEval: null,
                urlRightEval: null,
                textLeftEval: null,
                textRightEval: null,
                titleLeftEval: null,
                titleRightEval: null,
                flipEval: null,
                leftData: null,
                rightData: null,
                isFetchingDiffContent: false,
                diffEditorReady: false,
                debugMetrics: createFileDiffDebugMetrics(),
                monacoMetrics: null,
                _perfSessionStart: null,
            };
        },
        created() {
            this.editorId = utils.generateUUID();
        },
        mounted() {
            this._observeLayoutTargets();
        },
        beforeUnmount() {
            this._disconnectLayoutObservers();
        },
        watch: {
            isFetchingDiffContent(loading) {
                if (!loading)
                    this._scheduleEditorLayout();
            },
            isvisible(visible) {
                if (visible)
                    this._scheduleEditorLayout();
            },
        },
        methods: {
            _disconnectLayoutObservers() {
                if (this._layoutResizeObserver) {
                    this._layoutResizeObserver.disconnect();
                    this._layoutResizeObserver = null;
                }
                if (this._layoutEditorRaf) {
                    cancelAnimationFrame(this._layoutEditorRaf);
                    this._layoutEditorRaf = null;
                }
            },
            _observeLayoutTargets() {
                this._disconnectLayoutObservers();
                if (typeof ResizeObserver === 'undefined')
                    return;
                this._layoutResizeObserver = new ResizeObserver(() => this._scheduleEditorLayout());
                const targets = [this.$refs.layoutRoot, this.$refs.editorWrap].filter(Boolean);
                for (const el of targets)
                    this._layoutResizeObserver.observe(el);
            },
            _scheduleEditorLayout() {
                if (this._layoutEditorRaf)
                    cancelAnimationFrame(this._layoutEditorRaf);
                this._layoutEditorRaf = requestAnimationFrame(() => {
                    this._layoutEditorRaf = null;
                    this.$refs.diffEditor?.layout?.();
                });
            },
            async preRenderComplete() {
                this.isFetchingDiffContent = true;
                this._perfSessionStart = performance.now();
                this.debugMetrics = createFileDiffDebugMetrics();
                this.monacoMetrics = null;

                const loadLeft = async () => {
                    if (this.controlData.LeftType != 'Url')
                        return;
                    const t0 = performance.now();
                    this.leftData = await this.fetchSideUrl(this.urlLeft);
                    this.debugMetrics.leftLoadMs = performance.now() - t0;
                    this.debugMetrics.leftSource = 'url';
                };
                const loadRight = async () => {
                    if (this.controlData.RightType != 'Url')
                        return;
                    const t0 = performance.now();
                    this.rightData = await this.fetchSideUrl(this.urlRight);
                    this.debugMetrics.rightLoadMs = performance.now() - t0;
                    this.debugMetrics.rightSource = 'url';
                };

                try {
                    const tFetch = performance.now();
                    await Promise.all([loadLeft(), loadRight()]);
                    this.debugMetrics.fetchTotalMs = performance.now() - tFetch;

                    if (this.controlData.LeftType != 'Url')
                        this.debugMetrics.leftSource = 'text';
                    if (this.controlData.RightType != 'Url')
                        this.debugMetrics.rightSource = 'text';

                    const tFmtLeft = performance.now();
                    const leftFormatted = this.formatSideContent(this.leftData || this.textLeft);
                    this.debugMetrics.leftFormatMs = performance.now() - tFmtLeft;
                    this.debugMetrics.leftLines = countLines(leftFormatted);
                    this.debugMetrics.leftBytes = leftFormatted.length;

                    const tFmtRight = performance.now();
                    const rightFormatted = this.formatSideContent(this.rightData || this.textRight);
                    this.debugMetrics.rightFormatMs = performance.now() - tFmtRight;
                    this.debugMetrics.rightLines = countLines(rightFormatted);
                    this.debugMetrics.rightBytes = rightFormatted.length;

                    this.finishRenderHandler(this);
                } finally {
                    await this.$nextTick();
                    this.isFetchingDiffContent = false;
                    await this.$nextTick();
                    this._observeLayoutTargets();
                    this._scheduleEditorLayout();
                    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
                    this._scheduleEditorLayout();
                    const tReveal = performance.now();
                    this.$refs.diffEditor?.revealFirstDiff?.();
                    this.debugMetrics.revealFirstDiffMs = performance.now() - tReveal;
                    this.syncMonacoMetrics();
                }
            },
            fetchSideUrl(url) {
                return api.apiRequest({
                    method: 'GET',
                    url,
                    flatten: true,
                    doNotUseWebsocket: true,
                });
            },
            syncMonacoMetrics() {
                const m = this.$refs.diffEditor?.getPerfMetrics?.();
                if (m)
                    this.monacoMetrics = m;
            },
            onMonacoMetricsUpdate(metrics) {
                this.monacoMetrics = metrics;
                if (metrics?.firstDiffComputeMs != null && this._perfSessionStart != null)
                    this.debugMetrics.totalToFirstDiffMs = performance.now() - this._perfSessionStart;
            },
            formatSideContent(res) {
                if (!res) return '';
                switch (this.controlData.TextType) {
                    case 'json':
                        if (typeof res === 'string')
                            return JSON.stringify(JSON.parse(res), null, 4);
                        if (typeof res === 'object')
                            return JSON.stringify(res, null, 4);
                        return res.toString();
                    default:
                        return res;
                }
            },
            onDiffEditorInit() {
                this.diffEditorReady = true;
                this.syncMonacoMetrics();
                this.$nextTick(() => {
                    this._observeLayoutTargets();
                    this._scheduleEditorLayout();
                });
            },
            NextDiff() {
                this.$refs.diffEditor?.goToNextDiff?.();
            },
            PrevDiff() {
                this.$refs.diffEditor?.goToPreviousDiff?.();
            },
            formatSourceTiming(source, ms) {
                if (source === 'text')
                    return 'inline text';
                if (source === 'url')
                    return formatMs(ms);
                return '—';
            },
            formatSizeLine(lines, bytes) {
                const linePart = lines == null ? '—' : `${lines.toLocaleString()} lines`;
                const bytePart = formatBytes(bytes);
                return `${linePart}, ${bytePart}`;
            },
        },
        computed: {
            urlLeft() {
                try {
                    if (!this.urlLeftEval && this.controlData.LeftUrl) {
                        this.urlLeftEval = utils.compile(this, this.controlData.LeftUrl);
                    }
                    return this.urlLeftEval ? utils.evaluate(this.urlLeftEval, this) : '';
                } catch (e) {
                    utils.warn('LeftUrl could not evaluate expression: ' + this.controlData.LeftUrl + '; ' + e);
                    return '';
                }
            },
            urlRight() {
                try {
                    if (!this.urlRightEval && this.controlData.RightUrl) {
                        this.urlRightEval = utils.compile(this, this.controlData.RightUrl);
                    }
                    return this.urlRightEval ? utils.evaluate(this.urlRightEval, this) : '';
                } catch (e) {
                    utils.warn('RightUrl could not evaluate expression: ' + this.controlData.RightUrl + '; ' + e);
                    return '';
                }
            },
            textLeft() {
                try {
                    if (!this.textLeftEval && this.controlData.LeftText) {
                        this.textLeftEval = utils.compile(this, this.controlData.LeftText);
                    }
                    return this.textLeftEval ? api.standardizeUrl(utils.evaluate(this.textLeftEval, this)) : '';
                } catch (e) {
                    utils.warn('LeftText could not evaluate expression: ' + this.controlData.LeftText + '; ' + e);
                    return '';
                }
            },
            textRight() {
                try {
                    if (!this.textRightEval && this.controlData.RightText) {
                        this.textRightEval = utils.compile(this, this.controlData.RightText);
                    }
                    return this.textRightEval ? api.standardizeUrl(utils.evaluate(this.textRightEval, this)) : '';
                } catch (e) {
                    utils.warn('RightText could not evaluate expression: ' + this.controlData.RightText + '; ' + e);
                    return '';
                }
            },
            leftString() {
                return this.formatSideContent(this.leftData || this.textLeft);
            },
            rightString() {
                return this.formatSideContent(this.rightData || this.textRight);
            },
            titleLeft() {
                try {
                    if (!this.titleLeftEval && this.controlData.LeftTitle) {
                        this.titleLeftEval = utils.compile(this, this.controlData.LeftTitle);
                    }
                    return this.titleLeftEval ? utils.evaluate(this.titleLeftEval, this) : '';
                } catch (e) {
                    utils.warn('LeftTitle could not evaluate expression: ' + this.controlData.LeftTitle + '; ' + e);
                    return '';
                }
            },
            titleRight() {
                try {
                    if (!this.titleRightEval && this.controlData.RightTitle) {
                        this.titleRightEval = utils.compile(this, this.controlData.RightTitle);
                    }
                    return this.titleRightEval ? utils.evaluate(this.titleRightEval, this) : '';
                } catch (e) {
                    utils.warn('RightTitle could not evaluate expression: ' + this.controlData.RightTitle + '; ' + e);
                    return '';
                }
            },
            flipLeftToRightExpression() {
                try {
                    if (!this.flipEval && this.controlData.FlipLeftToRightExpression) {
                        this.flipEval = utils.compileExpression(this, this.controlData.FlipLeftToRightExpression);
                    }
                    return this.flipEval ? !!utils.evaluate(this.flipEval, this) : false;
                } catch (e) {
                    utils.warn('FlipLeftToRightExpression could not evaluate expression: ' + this.controlData.FlipLeftToRightExpression + '; ' + e);
                    return false;
                }
            },
            displayOriginalContent() {
                return this.flipLeftToRightExpression ? this.rightString : this.leftString;
            },
            displayModifiedContent() {
                return this.flipLeftToRightExpression ? this.leftString : this.rightString;
            },
            displayTitleLeft() {
                return this.flipLeftToRightExpression ? this.titleRight : this.titleLeft;
            },
            displayTitleRight() {
                return this.flipLeftToRightExpression ? this.titleLeft : this.titleRight;
            },
            showPaneTitles() {
                return !!(this.displayTitleLeft || this.displayTitleRight);
            },
            debugMetricRows() {
                const d = this.debugMetrics;
                const m = this.monacoMetrics;
                const rows = [
                    { key: 'left-load', label: 'Left load', value: this.formatSourceTiming(d.leftSource, d.leftLoadMs) },
                    { key: 'right-load', label: 'Right load', value: this.formatSourceTiming(d.rightSource, d.rightLoadMs) },
                    { key: 'fetch-total', label: 'Fetch total (parallel)', value: formatMs(d.fetchTotalMs) },
                    { key: 'left-format', label: 'Left format', value: formatMs(d.leftFormatMs) },
                    { key: 'right-format', label: 'Right format', value: formatMs(d.rightFormatMs) },
                    { key: 'left-size', label: 'Left size', value: this.formatSizeLine(d.leftLines, d.leftBytes) },
                    { key: 'right-size', label: 'Right size', value: this.formatSizeLine(d.rightLines, d.rightBytes) },
                ];
                if (m) {
                    rows.push(
                        { key: 'monaco-import', label: 'Monaco import', value: formatMs(m.monacoImportMs) },
                        { key: 'dom-layout', label: 'DOM layout wait', value: formatMs(m.domLayoutWaitMs) },
                        { key: 'model-create', label: 'Model create', value: formatMs(m.modelCreateMs) },
                        { key: 'diff-editor-create', label: 'Diff editor create', value: formatMs(m.diffEditorCreateMs) },
                        { key: 'set-model', label: 'Set model', value: formatMs(m.setModelMs) },
                        { key: 'editor-mount', label: 'Editor mount', value: formatMs(m.editorMountMs) },
                        { key: 'first-diff', label: 'First diff compute', value: formatMs(m.firstDiffComputeMs) },
                        { key: 'last-diff', label: 'Last diff update', value: formatMs(m.lastDiffComputeMs) },
                        { key: 'diff-updates', label: 'Diff update count', value: m.diffUpdateCount ?? '—' },
                        { key: 'monaco-orig', label: 'Monaco original', value: this.formatSizeLine(m.originalLines, m.originalBytes) },
                        { key: 'monaco-mod', label: 'Monaco modified', value: this.formatSizeLine(m.modifiedLines, m.modifiedBytes) },
                        { key: 'language', label: 'Language', value: m.language || '—' },
                    );
                    if (m.loadError)
                        rows.push({ key: 'monaco-error', label: 'Monaco error', value: m.loadError });
                }
                rows.push(
                    { key: 'reveal-first', label: 'Reveal first diff', value: formatMs(d.revealFirstDiffMs) },
                    { key: 'total-first-diff', label: 'Total → first diff', value: formatMs(d.totalToFirstDiffMs) },
                );
                return rows;
            },
        },
    };
</script>

<style scoped>
.c-FileDiffv2 {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    box-sizing: border-box;
}

.c-FileDiff__inner,
.c-FileDiffv2__inner {
    position: relative;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1 1 auto;
}

.c-FileDiffv2__toolbar {
    display: flex;
    gap: 4px;
    padding: 4px 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    flex: 0 0 auto;
}

.c-FileDiffv2__editor-wrap {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
}

.c-FileDiffv2__pane-titles {
    display: flex;
    flex: 0 0 auto;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.c-FileDiffv2__pane-title {
    flex: 1 1 50%;
    min-width: 0;
    padding: 4px 12px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    background: rgba(0, 0, 0, 0.03);
}

.c-FileDiffv2__pane-title--left {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.c-FileDiffv2__pane-title:empty {
    padding-top: 0;
    padding-bottom: 0;
    background: transparent;
}

.c-FileDiffv2__editor {
    flex: 1 1 auto;
    min-height: 0;
}

.c-FileDiff__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;
    padding: 24px;
}

.c-FileDiffv2__debug-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 4;
    opacity: 0.75;
}

.c-FileDiffv2__debug-btn:hover {
    opacity: 1;
}

.c-FileDiffv2__debug-tooltip {
    max-width: 380px;
}

.c-FileDiffv2__debug-tooltip-inner {
    padding: 2px 0;
    font-size: 12px;
    line-height: 1.35;
}

.c-FileDiffv2__debug-tooltip-title {
    font-weight: 600;
    margin-bottom: 6px;
    font-size: 13px;
}

.c-FileDiffv2__debug-table {
    border-collapse: collapse;
    width: 100%;
}

.c-FileDiffv2__debug-table th {
    text-align: left;
    font-weight: 500;
    padding: 2px 10px 2px 0;
    vertical-align: top;
    white-space: nowrap;
    opacity: 0.9;
}

.c-FileDiffv2__debug-table td {
    text-align: right;
    padding: 2px 0;
    font-variant-numeric: tabular-nums;
}
</style>
